/**
 * VSI.3.x — Renderer (reine Darstellung, KEINE Fachlogik).
 *
 * Adaptiert ein Visual Learning Object auf seine Anzeige-Komponente. Renderer treffen keine
 * inhaltlichen/rechtlichen/didaktischen Entscheidungen — sie stellen ausschließlich `vlo.payload` dar.
 * Keine Ableitung, keine Berechnung, keine Priorisierung, keine Übersetzung.
 */
import { Check, ShieldCheck, Target, TriangleAlert, X } from "lucide-react";

import { SafetySymbols } from "@/components/SafetySymbols";
import { useResolvedT, translateContentHeading } from "@/i18n/monteur";
import { getHazardIcon } from "./hazardIcon";
import { HazardIllustration } from "./hazardIllustration";
import { LehrbildImage } from "./lehrbild";

export function SafetySymbolsRenderer({ vlo, lang = null }) {
  // P0-FIX (Juni 2026): `lang` (Kontext-Sprache aus VisualObjects) an SafetySymbols
  // DURCHREICHEN — exakt wie der StatusBadge-Owner-Fix. Owner erzwingt `lang="de"`,
  // sonst (Mitarbeiterflow, lang=null) bleibt die Session-Sprache maßgeblich.
  return <SafetySymbols symbols={vlo?.payload?.symbols} lang={lang} />;
}

// VSI.3.3 — PSA-Zusammenfassung (anchor='end'): reine Liste der vorhandenen PSA-Einträge.
export function PsaSummaryRenderer({ vlo, lang = null }) {
  const t = useResolvedT(lang);
  const items = Array.isArray(vlo?.payload?.items) ? vlo.payload.items : [];
  if (items.length === 0) return null;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50/70 p-4"
      data-testid="psa-summary"
    >
      <div className="flex items-start gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          {t("vlo.psaSummary.title")}
        </div>
      </div>
      <ul className="flex flex-col gap-1.5">
        {items.map((it) => (
          <li
            key={it.id}
            className="text-sm text-slate-700"
            data-testid={`psa-summary-item-${it.id}`}
          >
            {it.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// VSI.5-A (DEMO-SAFE-8) — Gefahrenmarkierungen (anchor='in'): jede Gefährdung als eigene,
// sofort erfassbare Karte. Verwendet AUSSCHLIESSLICH die vorhandenen Felder category/description
// aus payload.items[]. Keine neuen Inhalte, keine Ableitung, keine Interpretation, kein Risiko/
// keine Maßnahmen/keine PSA. Fail-closed: ein Item ohne jeden Klartext wird ausgeblendet (keine
// Platzhalter), leere Liste -> null (kein Crash).
export function HazardMarkersRenderer({ vlo, sourceVlo = null, lang = null }) {
  const t = useResolvedT(lang);
  const hasText = (v) => typeof v === "string" && v.trim().length > 0;
  const items = (Array.isArray(vlo?.payload?.items) ? vlo.payload.items : []).filter(
    (it) => it && (hasText(it.category) || hasText(it.description))
  );
  if (items.length === 0) return null;
  // AP-LANG-SOURCE: deutscher Quelltext je Gefährdung (per stabiler id) für den
  // sprachneutralen Lehrbild-Lookup; die Anzeige bleibt in Mitarbeiter-Sprache.
  const srcById = {};
  (Array.isArray(sourceVlo?.payload?.items) ? sourceVlo.payload.items : []).forEach((s) => {
    if (s && s.id) srcById[s.id] = s.description || "";
  });
  return (
    <div data-testid="hazard-markers">
      <div className="flex items-center gap-2 mb-3">
        <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0" />
        <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">
          {t("vlo.hazardMarkers.title")}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((it) => {
          // VSI.5-B: kategorie-spezifisches Icon (nur Wiedererkennung), fail-closed -> neutrales Warn-Icon.
          const Icon = getHazardIcon(it.category);
          return (
          <div
            key={it.id}
            className="rounded-xl border border-amber-200 bg-amber-50/60 p-5"
            data-testid={`hazard-marker-${it.id}`}
          >
            {/* IMPLEMENT-14: KI-Lehrbild aus dem Welt-Manifest (read-only, beweisneutral).
                Vorhanden -> ersetzt den SVG-Platzhalter; sonst fail-closed Fallback auf die
                aussagebasierte SVG-Skizze (IMPLEMENT-10). */}
            <LehrbildImage
              text={it.description}
              lookupText={srcById[it.id]}
              testId={`hazard-marker-lehrbild-${it.id}`}
              className="mb-3 aspect-[16/9] overflow-hidden rounded-lg border border-amber-200 bg-white"
              fallback={
                <HazardIllustration
                  text={it.description}
                  category={it.category}
                  testId={`hazard-marker-illustration-${it.id}`}
                />
              }
            />
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 shrink-0">
                <Icon
                  className="w-4 h-4 text-amber-600"
                  aria-hidden="true"
                  data-testid={`hazard-marker-icon-${it.id}`}
                />
              </span>
              {hasText(it.category) ? (
                <span
                  className="font-semibold text-slate-900 leading-snug"
                  data-testid={`hazard-marker-category-${it.id}`}
                >
                  {it.category}
                </span>
              ) : null}
            </div>
            {hasText(it.description) ? (
              <p
                className="mt-3 text-sm text-slate-700 leading-relaxed"
                data-testid={`hazard-marker-desc-${it.id}`}
              >
                {it.description}
              </p>
            ) : null}
          </div>
          );
        })}
      </div>
    </div>
  );
}


// IMPLEMENT-4 / S3 — Lernfokus (anchor='pre'): ruhige Lernkarte, reine Darstellung der
// bereits abgeleiteten payload.focus[] (verbatim text/source). KEINE neuen Inhalte, keine
// Ableitung/Interpretation/Priorisierung, keine Beweiswirkung. Fail-closed: ein Eintrag ohne
// Klartext wird ausgeblendet (keine Platzhalter), leere Liste -> null (kein Crash).
// Hinweis: existiert + registriert, wird aber NICHT live, da die Seiten-Consumer per Whitelist
// filtern (Welt/MonteurAufgabe) bzw. nur bekannte Typen lesen (Plakat).
export function LearningFocusRenderer({ vlo, lang = null }) {
  const t = useResolvedT(lang);
  const hasText = (v) => typeof v === "string" && v.trim().length > 0;
  const focus = (Array.isArray(vlo?.payload?.focus) ? vlo.payload.focus : []).filter(
    (f) => f && hasText(f.text)
  );
  if (focus.length === 0) return null;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50/70 p-4"
      data-testid="learning-focus"
    >
      <div className="flex items-start gap-2 mb-3">
        <Target className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          {t("vlo.learningFocus.title")}
        </div>
      </div>
      <ul className="flex flex-col gap-1.5">
        {focus.map((f, idx) => (
          <li
            key={`${idx}-${f.text}`}
            className="text-sm text-slate-700"
            data-testid={`learning-focus-item-${idx}`}
          >
            {f.source === "content" ? translateContentHeading(t, f.text) : f.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

// IMPLEMENT-8 / VLO-RICHTIG-FALSCH Stufe 2 — Piktogramm-Karten. Jede bereits
// vorhandene Aussage (Schutzmaßnahme = Richtig, Hauptgefahr = Falsch) wird als eigene,
// ruhige Karte dargestellt: bestehendes Piktogramm + sprachneutraler ✓/✗-Marker +
// Originaltext (verbatim). Falsch-Karten nutzen die deterministische Gefahren-Icon-
// Zuordnung (getHazardIcon, fail-closed -> neutrales Warn-Icon); Richtig-Karten ein
// neutrales Schutz-Symbol (ShieldCheck), da kein deterministisches Positiv-Mapping
// existiert (Fallback-Regel). KEINE neuen Inhalte, keine Ableitung/Umformulierung/
// Priorisierung, keine Szenen/Personen, keine Animation. Fail-closed: Karte ohne Text
// entfällt; gar keine Texte -> null.
function RichtigFalschCard({ text, lookupText, mode, Icon, testId }) {
  const correct = mode === "richtig";
  return (
    <div
      className={`rounded-xl border p-4 ${
        correct ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"
      }`}
      data-testid={testId}
    >
      {/* IMPLEMENT-14: KI-Lehrbild (read-only) oberhalb der Karte, falls im Welt-Manifest
          vorhanden; sonst fail-closed (nur Piktogramm + Text wie bisher). */}
      <LehrbildImage
        text={text}
        lookupText={lookupText}
        testId={`${testId}-lehrbild`}
        className={`mb-3 aspect-[16/9] overflow-hidden rounded-lg border bg-white ${
          correct ? "border-emerald-200" : "border-rose-200"
        }`}
      />
      <div className="flex items-start gap-3">
        <span className="relative shrink-0">
          <span
            className={`flex h-11 w-11 items-center justify-center rounded-full ${
              correct ? "bg-emerald-100" : "bg-rose-100"
            }`}
          >
            <Icon
              className={`w-5 h-5 ${correct ? "text-emerald-700" : "text-rose-700"}`}
              aria-hidden="true"
            />
          </span>
          <span
            className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${
              correct ? "bg-emerald-600" : "bg-rose-600"
            }`}
          >
            {correct ? (
              <Check className="w-3 h-3 text-white" aria-hidden="true" />
            ) : (
              <X className="w-3 h-3 text-white" aria-hidden="true" />
            )}
          </span>
        </span>
        <p className="text-sm leading-relaxed text-slate-800 pt-1.5">{text}</p>
      </div>
    </div>
  );
}

export function RichtigFalschRenderer({ vlo, sourceVlo = null, lang = null }) {
  const t = useResolvedT(lang);
  const hasText = (v) => typeof v === "string" && v.trim().length > 0;
  const pick = (arr) =>
    (Array.isArray(arr) ? arr : []).filter((it) => it && hasText(it.text));
  const richtig = pick(vlo?.payload?.richtig);
  const falsch = pick(vlo?.payload?.falsch);
  if (richtig.length === 0 && falsch.length === 0) return null;
  // AP-LANG-SOURCE: deutsche Quelltexte (positionsgleich) für den sprachneutralen
  // Lehrbild-Lookup; die Anzeige bleibt in Mitarbeiter-Sprache.
  const srcRichtig = pick(sourceVlo?.payload?.richtig);
  const srcFalsch = pick(sourceVlo?.payload?.falsch);
  return (
    <div data-testid="richtig-falsch">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">
        {t("vlo.richtigFalsch.title")}
      </div>

      {richtig.length > 0 && (
        <div className="mb-4" data-testid="richtig-falsch-richtig">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              {t("vlo.richtigFalsch.correct")}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {richtig.map((it, i) => (
              <RichtigFalschCard
                key={`r-${i}`}
                text={it.text}
                lookupText={srcRichtig[i]?.text}
                mode="richtig"
                Icon={ShieldCheck}
                testId={`richtig-falsch-richtig-card-${i}`}
              />
            ))}
          </div>
        </div>
      )}

      {falsch.length > 0 && (
        <div data-testid="richtig-falsch-falsch">
          <div className="flex items-center gap-2 mb-2">
            <TriangleAlert className="w-4 h-4 text-rose-700 shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-wider text-rose-700">
              {t("vlo.richtigFalsch.wrong")}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {falsch.map((it, i) => (
              <RichtigFalschCard
                key={`f-${i}`}
                text={it.text}
                lookupText={srcFalsch[i]?.text}
                mode="falsch"
                Icon={getHazardIcon(it.text)}
                testId={`richtig-falsch-falsch-card-${i}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}